import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import StepSelectUniversidad from './StepSelectUniversidad';
import StepSelectInvestigador from './StepSelectInvestigador';
import StepPublicaciones from './StepPublicaciones';
import StepPublicacionesExtra from './StepPublicacionesExtra';

const STEPS = [
  { label: 'Universidad', icon: 'account_balance' },
  { label: 'Investigador', icon: 'person_search' },
  { label: 'Publicaciones', icon: 'article' },
  { label: 'Publicaciones extra', icon: 'search' },
];

function Stepper({ currentStep }) {
  return (
    <div className="flex items-center justify-center mb-10">
      {STEPS.map((step, idx) => {
        const stepNumber = idx + 1;
        const isCompleted = currentStep > stepNumber;
        const isActive = currentStep === stepNumber;
        return (
          <React.Fragment key={step.label}>
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shadow transition-all ${
                  isCompleted
                    ? 'bg-green-500 text-white'
                    : isActive
                    ? 'bg-[#2D5BA6] text-white ring-4 ring-blue-200 dark:ring-blue-900'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                }`}
              >
                {isCompleted ? (
                  <span className="material-icons-round text-lg">check</span>
                ) : (
                  <span className="material-icons-round text-lg">{step.icon}</span>
                )}
              </div>
              <span
                className={`text-xs font-semibold ${
                  isActive ? 'text-[#2D5BA6] dark:text-[#5B8BD9]' : 'text-slate-400 dark:text-slate-500'
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-3 mt-[-1rem] transition-all ${
                  currentStep > stepNumber ? 'bg-green-400' : 'bg-slate-200 dark:bg-slate-700'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function BusquedaPublicaciones() {
  const location = useLocation();
  const navigate = useNavigate();
  const prefillConsumedRef = useRef(false);
  const [step, setStep] = useState(1);

  // Data state
  const [universidades, setUniversidades] = useState([]);
  const [loadingUniversidades, setLoadingUniversidades] = useState(false);
  const [selectedUniversidad, setSelectedUniversidad] = useState(null);

  const [investigadores, setInvestigadores] = useState([]);
  const [loadingInvestigadores, setLoadingInvestigadores] = useState(false);
  const [selectedInvestigador, setSelectedInvestigador] = useState(null);

  const [publicaciones, setPublicaciones] = useState([]);
  const [loadingPublicaciones, setLoadingPublicaciones] = useState(false);
  const [publicacionesError, setPublicacionesError] = useState(null);
  const [savedPublicaciones, setSavedPublicaciones] = useState([]);

  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [initialAuthorSearchText, setInitialAuthorSearchText] = useState('');

  const resetToStart = useCallback((message = null) => {
    setStep(1);
    setSelectedUniversidad(null);
    setSelectedInvestigador(null);
    setInvestigadores([]);
    setPublicaciones([]);
    setPublicacionesError(null);
    setSavedPublicaciones([]);
    setSaveResult(null);
    setSuccessMessage(message);
    setInitialAuthorSearchText('');
  }, []);

  // Load universities on mount
  useEffect(() => {
    setLoadingUniversidades(true);
    api.get('/api/universities')
      .then((res) => setUniversidades(res.data))
      .catch((err) => console.error('Error loading universities:', err))
      .finally(() => setLoadingUniversidades(false));
  }, []);

  // Step 1 → 2: Select university
  const handleSelectUniversidad = useCallback((univ) => {
    setSuccessMessage(null);
    setSelectedUniversidad(univ);
    setSelectedInvestigador(null);
    setInvestigadores([]);
    setPublicaciones([]);
    setSaveResult(null);
    setStep(2);

    setLoadingInvestigadores(true);
    api.get('/api/investigadores/', { params: { universidad_id: univ.id, limit: 100000 } })
      .then((res) => setInvestigadores(res.data))
      .catch((err) => console.error('Error loading investigators:', err))
      .finally(() => setLoadingInvestigadores(false));
  }, []);

  // Step 2 → 3: Select investigator
  const handleSelectInvestigador = useCallback((inv) => {
    setSuccessMessage(null);
    setSelectedInvestigador(inv);
    setPublicaciones([]);
    setPublicacionesError(null);
    setSaveResult(null);
    setSavedPublicaciones([]);
    setStep(3);

    // Solo cargar publicaciones guardadas en la base de datos local
    setLoadingPublicaciones(true);
    api.get(`/api/investigadores/${inv.id}/publicaciones`, { params: { skip: 0, limit: 1000 } })
      .then((res) => {
        setSavedPublicaciones(Array.isArray(res.data) ? res.data : []);
        setPublicaciones([]); // No cargar de OpenAlex en este paso
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || 'Error al cargar publicaciones';
        setPublicacionesError(msg);
      })
      .finally(() => setLoadingPublicaciones(false));
  }, []);

  // Step 3: Save selected publications
  const handleSave = useCallback(async (toSave) => {
    if (!selectedInvestigador || toSave.length === 0) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const res = await api.post(`/api/investigadores/${selectedInvestigador.id}/publicaciones`, {
        publications: toSave,
      });
      setSaveResult(res.data);

      // Refrescar para que "Ya en base" quede correcto
      const savedRes = await api.get(`/api/investigadores/${selectedInvestigador.id}/publicaciones`, {
        params: { skip: 0, limit: 1000 },
      });
      setSavedPublicaciones(Array.isArray(savedRes.data) ? savedRes.data : []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al guardar publicaciones';
      setSaveResult({ saved: 0, duplicates: 0, errors: [{ title: 'Error', error: msg }] });
    } finally {
      setSaving(false);
    }
  }, [selectedInvestigador]);

  const handleGoExtra = useCallback(() => {
    setStep(4);
  }, []);

  // Back handlers
  const handleBackToUniversidad = useCallback(() => {
    resetToStart();
  }, [resetToStart]);

  const handleBackToInvestigador = useCallback(() => {
    setStep(2);
    setSelectedInvestigador(null);
    setPublicaciones([]);
    setPublicacionesError(null);
    setSaveResult(null);
  }, []);

  const handleBackToPublicaciones = useCallback(() => {
    setStep(3);
  }, []);

  useEffect(() => {
    if (step === 4 && saveResult && !saveResult.errors?.length && saveResult.saved > 0) {
      resetToStart(`Se guardaron ${saveResult.saved} publicaciones extra correctamente.`);
    }
  }, [step, saveResult, resetToStart]);

  useEffect(() => {
    const prefill = location.state?.prefillExtra;
    if (!prefill || prefillConsumedRef.current || universidades.length === 0) return;

    prefillConsumedRef.current = true;

    const run = async () => {
      try {
        const universidad = universidades.find((u) => {
          if (prefill.universidadId && Number(u.id) === Number(prefill.universidadId)) return true;
          if (prefill.universidadName && u.name === prefill.universidadName) return true;
          return false;
        });

        if (!universidad) {
          throw new Error('No se encontró la universidad del investigador.');
        }

        setSelectedUniversidad(universidad);
        setSelectedInvestigador(null);
        setInvestigadores([]);
        setPublicaciones([]);
        setPublicacionesError(null);
        setSaveResult(null);
        setSavedPublicaciones([]);
        setInitialAuthorSearchText(prefill.authorName || '');

        setLoadingInvestigadores(true);
        const invRes = await api.get('/api/investigadores/', {
          params: { universidad_id: universidad.id, limit: 100000 },
        });
        const investigadoresList = Array.isArray(invRes.data) ? invRes.data : [];
        setInvestigadores(investigadoresList);

        const investigator =
          investigadoresList.find((inv) => Number(inv.id) === Number(prefill.investigadorId)) ||
          prefill.investigador ||
          null;

        if (!investigator) {
          throw new Error('No se encontró el investigador seleccionado.');
        }

        setSelectedInvestigador(investigator);
        setStep(4);

        setLoadingPublicaciones(true);
        const savedRes = await api.get(`/api/investigadores/${investigator.id}/publicaciones`, {
          params: { skip: 0, limit: 1000 },
        });
        setSavedPublicaciones(Array.isArray(savedRes.data) ? savedRes.data : []);
        setPublicaciones([]);
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'No se pudo abrir el flujo de publicaciones extra.';
        setPublicacionesError(msg);
        setStep(1);
      } finally {
        setLoadingInvestigadores(false);
        setLoadingPublicaciones(false);
        navigate(location.pathname, { replace: true, state: null });
      }
    };

    run();
  }, [location.pathname, location.state, navigate, universidades]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Búsqueda de Publicaciones
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm">
            Busca y guarda publicaciones de investigadores registrados en el sistema usando OpenAlex
          </p>
        </div>

        {successMessage && (
          <div className="mb-6 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm text-green-700 dark:text-green-300">
            {successMessage}
          </div>
        )}

        {/* Stepper */}
        <Stepper currentStep={step} />

        {/* Card */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-md border border-slate-200 dark:border-slate-800 p-6 md:p-8">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <StepSelectUniversidad
                  universidades={universidades}
                  loadingUniversidades={loadingUniversidades}
                  onSelect={handleSelectUniversidad}
                />
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <StepSelectInvestigador
                  universidad={selectedUniversidad}
                  investigadores={investigadores}
                  loading={loadingInvestigadores}
                  onSelect={handleSelectInvestigador}
                  onBack={handleBackToUniversidad}
                />
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <StepPublicaciones
                  universidad={selectedUniversidad}
                  investigador={selectedInvestigador}
                  publicaciones={publicaciones}
                  savedPublicaciones={savedPublicaciones}
                  loading={loadingPublicaciones}
                  loadingError={publicacionesError}
                  saving={saving}
                  saveResult={saveResult}
                  onBack={handleBackToInvestigador}
                  onSave={handleSave}
                  onGoExtra={handleGoExtra}
                />
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {selectedUniversidad && selectedInvestigador ? (
                  <StepPublicacionesExtra
                    universidad={selectedUniversidad}
                    investigador={selectedInvestigador}
                    savedPublicaciones={savedPublicaciones}
                    saving={saving}
                    saveResult={saveResult}
                    initialAuthorSearchText={initialAuthorSearchText}
                    onBack={handleBackToPublicaciones}
                    onSave={handleSave}
                  />
                ) : (
                  <div className="flex items-center justify-center py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-10 h-10 border-4 border-[#2D5BA6] border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Preparando publicaciones extra...
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default BusquedaPublicaciones;
